import * as THREE from 'three';
import type { SimEvent } from '../../sim/events';
import type { Shape } from '../../sim/shapes';
import type { Element } from '../../sim/types';
import { BeamEffect, CrackEffect, DiscEffect, type Effect, PillarEffect, RingEffect, SlashEffect, TELEGRAPH_COLORS, TelegraphEffect } from './Effects';
import { ParticleSystem, particleTextures } from './Particles';

export const ELEMENT_COLORS: Record<Element, THREE.Color> = {
  none: new THREE.Color(1.0, 0.92, 0.8),
  fire: new THREE.Color(1.0, 0.42, 0.12),
  water: new THREE.Color(0.45, 0.85, 1.0),
  lightning: new THREE.Color(0.65, 0.78, 1.0),
  earth: new THREE.Color(0.85, 0.65, 0.35),
  holy: new THREE.Color(1.0, 0.86, 0.45),
  dark: new THREE.Color(0.7, 0.3, 1.0),
};

interface ProjectileVis {
  sprite: THREE.Sprite;
  pos: THREE.Vector3;
  dir: THREE.Vector3;
  speed: number;
  color: THREE.Color;
  trailT: number;
  life: number;
}

interface AreaVis {
  tele: TelegraphEffect | null;
  origin: THREE.Vector3;
  shape: Shape;
  facing: number;
  color: THREE.Color;
  persistent: boolean;
  erupted: boolean;
  tickT: number;
  friendly: boolean;
}

export interface VfxHost {
  scene: THREE.Scene;
  heightAt(x: number, z: number): number;
  /** Actor world position for hit sparks (null if unknown). */
  actorPos(id: number, out: THREE.Vector3): THREE.Vector3 | null;
  actorHeight(id: number): number;
  hitFlash(id: number): void;
  shake(amount: number): void;
  screenFlash(color: number, peak: number, duration: number): void;
  isLocalPlayer(id: number): boolean;
  /** Hit-stop request in seconds. */
  hitStop(seconds: number): void;
  /** Current simulated position of a projectile (so homing and returning shots are drawn where they are). */
  projectilePos?(id: number): { x: number; z: number } | null;
}

/**
 * Converts simulation events into visual effects. Purely presentational: it
 * never feeds back into the simulation.
 */
export class VfxDirector {
  readonly add: ParticleSystem;
  readonly soft: ParticleSystem;
  private effects: Effect[] = [];
  private projectiles = new Map<number, ProjectileVis>();
  private areas = new Map<number, AreaVis>();
  private glowTex: THREE.Texture;
  private slashFlip = new Map<number, boolean>();
  private tmp = new THREE.Vector3();
  /** Quality scaling for particle counts. */
  density = 1;

  constructor(private host: VfxHost) {
    const tex = particleTextures();
    this.glowTex = tex.glow;
    this.add = new ParticleSystem(6000, tex.glow, THREE.AdditiveBlending);
    this.soft = new ParticleSystem(2500, tex.smoke, THREE.NormalBlending);
    host.scene.add(this.add.mesh, this.soft.mesh);
    this.keepPrograms();
  }

  private keepers: Effect[] = [];
  private keeperSprite: THREE.Sprite | null = null;

  /**
   * Effects create and dispose their materials; when the last effect of a
   * kind dies, three.js releases its shader program and the next spawn
   * recompiles it (a hitch in every fight). One zero-draw instance of each
   * effect stays in the scene so the programs stay resident.
   */
  private keepPrograms(): void {
    const o = new THREE.Vector3(0, -500, 0);
    const c = new THREE.Color(1, 1, 1);
    this.keepers = [
      new SlashEffect(o, 0, 1, 1, c),
      new RingEffect(o, 1, c),
      new DiscEffect(o, 1, c),
      new PillarEffect(o, 1, 1, c),
      new BeamEffect(o, 0, 1, 1, c),
      new CrackEffect(o, 1, c),
      new TelegraphEffect(o, 0, { kind: 'circle', radius: 1 }, c, 1, () => 0),
    ];
    for (const e of this.keepers) {
      const m = e.obj as THREE.Mesh;
      m.geometry.setDrawRange(0, 0);
      m.frustumCulled = false;
      m.name = 'vfx_keeper';
      this.host.scene.add(m);
    }
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.glowTex, color: c, blending: THREE.AdditiveBlending, depthWrite: false, transparent: true }));
    sprite.scale.setScalar(1e-6);
    sprite.position.copy(o);
    sprite.frustumCulled = false;
    sprite.name = 'vfx_keeper';
    this.keeperSprite = sprite;
    this.host.scene.add(sprite);
  }

  private addEffect(e: Effect): void {
    this.host.scene.add(e.obj);
    this.effects.push(e);
  }

  private colorFor(element?: Element, hue?: number): THREE.Color {
    if (element && element !== 'none') return ELEMENT_COLORS[element].clone();
    if (hue !== undefined) return new THREE.Color(hue);
    return ELEMENT_COLORS.none.clone();
  }

  private ground(x: number, z: number): THREE.Vector3 {
    return new THREE.Vector3(x, this.host.heightAt(x, z), z);
  }

  burst(pos: THREE.Vector3, color: THREE.Color, count: number, speed: number, opts: { up?: number; size?: number; life?: number; gravity?: number; spark?: boolean } = {}): void {
    const n = Math.round(count * this.density);
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const el = (Math.random() - 0.2) * 1.2;
      const s = speed * (0.35 + Math.random() * 0.8);
      const c = color.clone().multiplyScalar(1.5 + Math.random() * 1.5);
      this.add.spawn({
        x: pos.x,
        y: pos.y,
        z: pos.z,
        vx: Math.cos(a) * s,
        vy: (opts.up ?? 0.5) * s + el * s * 0.3,
        vz: Math.sin(a) * s,
        color: c,
        colorEnd: color.clone().multiplyScalar(0.4),
        size: (opts.size ?? 0.18) * (0.6 + Math.random() * 0.8),
        sizeEnd: 0.02,
        life: (opts.life ?? 0.45) * (0.6 + Math.random() * 0.8),
        gravity: opts.gravity ?? 6,
        drag: 2.5,
        stretch: opts.spark ? 1.4 : 0,
      });
    }
  }

  dust(pos: THREE.Vector3, radius: number, count: number, color = new THREE.Color(0.55, 0.5, 0.42)): void {
    const n = Math.round(count * this.density);
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const r = Math.random() * radius;
      this.soft.spawn({
        x: pos.x + Math.cos(a) * r,
        y: pos.y + 0.2,
        z: pos.z + Math.sin(a) * r,
        vx: Math.cos(a) * 1.5,
        vy: 0.6 + Math.random() * 0.8,
        vz: Math.sin(a) * 1.5,
        color,
        size: 0.8 + Math.random() * 0.8,
        sizeEnd: 2.2,
        life: 0.9 + Math.random() * 0.6,
        drag: 1.8,
        alpha: 0.55,
      });
    }
  }

  /** Visualises the damaging shape of an attack. */
  private shapeEffect(origin: THREE.Vector3, facing: number, shape: Shape, color: THREE.Color, actorId: number, heavy: boolean): void {
    switch (shape.kind) {
      case 'cone': {
        const flip = !(this.slashFlip.get(actorId) ?? false);
        this.slashFlip.set(actorId, flip);
        const o = origin.clone();
        o.y += 0.9;
        this.addEffect(new SlashEffect(o, facing + (shape.rotation ?? 0), shape.radius, shape.angle, color, 0.12, 0.16, flip, (Math.random() - 0.5) * 0.25));
        if (heavy) this.addEffect(new SlashEffect(o.clone().setY(o.y + 0.15), facing, shape.radius * 0.85, shape.angle * 0.9, color.clone().multiplyScalar(0.8), 0.16, 0.2, !flip));
        break;
      }
      case 'circle': {
        const off = shape.offset ?? 0;
        const c = this.ground(origin.x + Math.sin(facing) * off, origin.z + Math.cos(facing) * off);
        this.addEffect(new RingEffect(c, shape.radius, color, 0.32));
        this.addEffect(new DiscEffect(c, shape.radius * 0.9, color, 0.25, 0.9));
        if (shape.radius >= 3) {
          this.addEffect(new CrackEffect(c, shape.radius * 0.9, color, 1.8));
          this.dust(c, shape.radius * 0.6, 10);
        }
        this.burst(c.clone().setY(c.y + 0.4), color, 10 + shape.radius * 4, 5 + shape.radius, { up: 0.9, spark: true });
        break;
      }
      case 'rect': {
        const o = origin.clone();
        this.addEffect(new BeamEffect(o, facing + (shape.rotation ?? 0), shape.length, Math.max(0.6, shape.width), color, 0.28, shape.offset ?? 0, 1.0));
        const end = o.clone().add(new THREE.Vector3(Math.sin(facing) * shape.length, 1, Math.cos(facing) * shape.length));
        this.burst(end, color, 8, 5, { spark: true });
        break;
      }
      case 'ring': {
        const off = shape.offset ?? 0;
        const c = this.ground(origin.x + Math.sin(facing) * off, origin.z + Math.cos(facing) * off);
        this.addEffect(new RingEffect(c, shape.outer, color, 0.45, 0.35, shape.inner / shape.outer));
        break;
      }
    }
  }

  handle(ev: SimEvent): void {
    switch (ev.type) {
      case 'action': {
        if (ev.kind === 'hit' && ev.shape) {
          const o = this.ground(ev.origin.x, ev.origin.z);
          const heavy = ev.vfx === 'heavy';
          this.shapeEffect(o, ev.facing, ev.shape, this.colorFor(ev.element, ev.hue), ev.actorId, heavy);
        } else if (ev.kind === 'move') {
          const o = this.ground(ev.origin.x, ev.origin.z);
          this.dust(o, 0.6, 5);
          this.burst(o.clone().setY(o.y + 0.8), this.colorFor(undefined, ev.hue), 6, 3, { up: 0.2 });
        } else if (ev.kind === 'teleport') {
          const o = this.ground(ev.origin.x, ev.origin.z);
          this.addEffect(new PillarEffect(o, 0.7, 2.6, this.colorFor(undefined, ev.hue ?? 0x88bbff), 0.35));
          this.burst(o.clone().setY(o.y + 1), this.colorFor(undefined, ev.hue ?? 0x88bbff), 16, 4);
        } else if (ev.kind === 'self' || ev.kind === 'party') {
          const o = this.ground(ev.origin.x, ev.origin.z);
          const col = this.colorFor(undefined, ev.hue ?? 0xffd070);
          const r = ev.shape && ev.shape.kind === 'circle' ? ev.shape.radius : 1.5;
          this.addEffect(new RingEffect(o, Math.min(r, 8), col, 0.6, 0.12));
          for (let i = 0; i < 18 * this.density; i++) {
            const a = Math.random() * Math.PI * 2;
            this.add.spawn({ x: o.x + Math.cos(a) * 0.6, y: o.y + 0.2, z: o.z + Math.sin(a) * 0.6, vy: 2 + Math.random() * 2, color: col.clone().multiplyScalar(2), size: 0.16, sizeEnd: 0.02, life: 0.9, drag: 0.5 });
          }
        }
        break;
      }
      case 'projectile': {
        const color = this.colorFor(undefined, ev.hue ?? 0xffcc88);
        const mat = new THREE.SpriteMaterial({ map: this.glowTex, color: color.clone().multiplyScalar(3), blending: THREE.AdditiveBlending, depthWrite: false, transparent: true });
        const sprite = new THREE.Sprite(mat);
        const s = Math.max(0.5, ev.radius * 2.2);
        sprite.scale.set(s, s, s);
        const pos = new THREE.Vector3(ev.pos.x, this.host.heightAt(ev.pos.x, ev.pos.z) + ev.height, ev.pos.z);
        sprite.position.copy(pos);
        sprite.renderOrder = 13;
        this.host.scene.add(sprite);
        this.projectiles.set(ev.id, { sprite, pos, dir: new THREE.Vector3(ev.dir.x, 0, ev.dir.z), speed: ev.speed, color, trailT: 0, life: 6 });
        break;
      }
      case 'projectileEnd': {
        const p = this.projectiles.get(ev.id);
        if (p) {
          this.burst(p.pos, p.color, ev.hit ? 14 : 6, 4, { spark: true });
          this.host.scene.remove(p.sprite);
          p.sprite.material.dispose();
          this.projectiles.delete(ev.id);
        }
        break;
      }
      case 'area': {
        const origin = this.ground(ev.origin.x, ev.origin.z);
        const color = ev.friendly ? TELEGRAPH_COLORS.ally!.clone() : ev.telegraph !== 'none' ? TELEGRAPH_COLORS[ev.telegraph]!.clone() : this.colorFor(undefined, ev.hue);
        let tele: TelegraphEffect | null = null;
        if (ev.delay > 0.05 && ev.telegraph !== 'none') {
          tele = new TelegraphEffect(origin, ev.facing, ev.shape, color, ev.delay, (x, z) => this.host.heightAt(x, z));
          this.addEffect(tele);
        } else if (ev.delay > 0.05) {
          // Player point skills: a faint targeting ring.
          tele = new TelegraphEffect(origin, ev.facing, ev.shape, TELEGRAPH_COLORS.ally!, ev.delay, (x, z) => this.host.heightAt(x, z));
          this.addEffect(tele);
        }
        this.areas.set(ev.id, { tele, origin, shape: ev.shape, facing: ev.facing, color: this.colorFor(undefined, ev.hue ?? (ev.telegraph === 'red' ? 0xff5030 : undefined)), persistent: ev.duration > 0, erupted: false, tickT: 0, friendly: ev.friendly });
        break;
      }
      case 'areaErupt': {
        const a = this.areas.get(ev.id);
        const origin = this.ground(ev.origin.x, ev.origin.z);
        const color = a?.color ?? this.colorFor(undefined, ev.hue);
        a?.tele?.finish();
        if (a) a.erupted = true;
        this.eruption(origin, ev.facing, ev.shape, color, ev.vfx);
        break;
      }
      case 'areaEnd': {
        const a = this.areas.get(ev.id);
        a?.tele?.finish();
        this.areas.delete(ev.id);
        break;
      }
      case 'hit': {
        if (ev.heal || ev.shield || ev.amount <= 0) break;
        const p = this.host.actorPos(ev.tgtId, this.tmp);
        if (!p) break;
        const h = this.host.actorHeight(ev.tgtId);
        const pos = p.clone().setY(p.y + h * 0.55);
        const color = ev.crit ? new THREE.Color(1.0, 0.8, 0.3) : ELEMENT_COLORS[ev.element].clone();
        if (!ev.dot) {
          this.burst(pos, color, ev.crit ? 14 : 7, ev.crit ? 7 : 5, { spark: true, size: 0.14, life: 0.3 });
          this.host.hitFlash(ev.tgtId);
        }
        if (this.host.isLocalPlayer(ev.tgtId) && !ev.dot) this.host.shake(Math.min(0.35, 0.08 + ev.amount / 5000));
        if (ev.crit && this.host.isLocalPlayer(ev.srcId)) {
          this.host.shake(0.12);
          this.host.hitStop(0.035);
        }
        break;
      }
      case 'counter': {
        const p = this.host.actorPos(ev.tgtId, this.tmp);
        if (p) {
          const pos = p.clone().setY(p.y + this.host.actorHeight(ev.tgtId) * 0.5);
          const blue = new THREE.Color(0.35, 0.65, 1.0);
          this.burst(pos, blue, 50, 12, { spark: true, size: 0.22, life: 0.6 });
          this.addEffect(new RingEffect(p.clone(), 6, blue, 0.5, 0.2));
        }
        this.host.shake(0.45);
        this.host.hitStop(0.09);
        this.host.screenFlash(0x6aaaff, 0.25, 0.25);
        break;
      }
      case 'staggerBreak': {
        const p = this.host.actorPos(ev.id, this.tmp);
        if (p) {
          const purple = new THREE.Color(0.7, 0.4, 1.0);
          this.burst(p.clone().setY(p.y + 1.5), purple, 40, 9, { spark: true, size: 0.2, life: 0.7 });
          this.addEffect(new RingEffect(p.clone(), 8, purple, 0.6, 0.18));
        }
        this.host.shake(0.4);
        this.host.screenFlash(0xb080ff, 0.18, 0.3);
        break;
      }
      case 'partBreak': {
        const p = this.host.actorPos(ev.id, this.tmp);
        if (p) {
          const c = new THREE.Color(0.7, 0.9, 0.85);
          this.burst(p.clone().setY(p.y + 2), c, 40, 10, { gravity: 14, size: 0.25, life: 1.1 });
          this.dust(p.clone(), 2, 14);
        }
        this.host.shake(0.35);
        break;
      }
      case 'death': {
        const p = this.host.actorPos(ev.id, this.tmp);
        if (p) this.burst(p.clone().setY(p.y + 0.8), new THREE.Color(0.5, 0.2, 0.7), 16, 3, { up: 1.2, gravity: -1, life: 1.2, size: 0.2 });
        break;
      }
      case 'dash':
      case 'standUp': {
        const p = this.host.actorPos(ev.actorId, this.tmp);
        if (p) this.dust(p.clone(), 0.5, 6);
        break;
      }
      default:
        break;
    }
  }

  /** Ground eruption for delayed areas / explosions. */
  private eruption(origin: THREE.Vector3, facing: number, shape: Shape, color: THREE.Color, vfx?: string): void {
    const big = shape.kind === 'circle' ? shape.radius : shape.kind === 'ring' ? shape.outer : shape.kind === 'cone' ? shape.radius : shape.length / 2;
    if (vfx === 'pillar' || vfx === 'meteor') {
      const r = shape.kind === 'circle' ? shape.radius : 2;
      this.addEffect(new PillarEffect(origin, r * 0.8, 6 + r, color, 0.7));
    }
    this.shapeEffect(origin, facing, shape, color, -1, true);
    this.addEffect(new DiscEffect(origin, big * 1.2, color, 0.35, 1.3));
    if (big >= 4) this.host.shake(Math.min(0.5, 0.1 + big * 0.03));
  }

  update(dt: number): void {
    for (let i = this.effects.length - 1; i >= 0; i--) {
      const e = this.effects[i] as Effect;
      if (!e.update(dt)) {
        this.host.scene.remove(e.obj);
        e.dispose();
        this.effects.splice(i, 1);
      }
    }
    for (const [id, p] of this.projectiles) {
      const sim = this.host.projectilePos?.(id);
      if (sim) {
        p.pos.x = sim.x;
        p.pos.z = sim.z;
      } else p.pos.addScaledVector(p.dir, p.speed * dt);
      p.pos.y = this.host.heightAt(p.pos.x, p.pos.z) + 1.1;
      p.sprite.position.copy(p.pos);
      p.life -= dt;
      p.trailT -= dt;
      if (p.trailT <= 0) {
        p.trailT = 0.016;
        this.add.spawn({ x: p.pos.x, y: p.pos.y, z: p.pos.z, vx: (Math.random() - 0.5) * 0.6, vy: (Math.random() - 0.5) * 0.6, vz: (Math.random() - 0.5) * 0.6, color: p.color.clone().multiplyScalar(2), colorEnd: p.color.clone().multiplyScalar(0.3), size: 0.35, sizeEnd: 0.05, life: 0.35 });
      }
      if (p.life <= 0) {
        this.host.scene.remove(p.sprite);
        p.sprite.material.dispose();
        this.projectiles.delete(id);
      }
    }
    // Persistent areas emit ambient particles while active.
    for (const a of this.areas.values()) {
      if (!a.persistent || !a.erupted) continue;
      a.tickT -= dt;
      if (a.tickT > 0) continue;
      a.tickT = 0.05;
      const r = a.shape.kind === 'circle' ? a.shape.radius : a.shape.kind === 'ring' ? a.shape.outer : 2.5;
      const ang = Math.random() * Math.PI * 2;
      const rr = Math.sqrt(Math.random()) * r;
      const x = a.origin.x + Math.cos(ang) * rr;
      const z = a.origin.z + Math.sin(ang) * rr;
      this.add.spawn({ x, y: this.host.heightAt(x, z) + 0.1, z, vy: 1.5 + Math.random() * 2, color: a.color.clone().multiplyScalar(2.2), colorEnd: a.color.clone().multiplyScalar(0.2), size: 0.35, sizeEnd: 0.05, life: 0.8, drag: 0.8 });
    }
    this.add.update(dt);
    this.soft.update(dt);
  }

  clear(): void {
    for (const e of this.keepers) {
      this.host.scene.remove(e.obj);
      e.dispose();
    }
    this.keepers = [];
    if (this.keeperSprite) {
      this.host.scene.remove(this.keeperSprite);
      this.keeperSprite.material.dispose();
      this.keeperSprite = null;
    }
    for (const e of this.effects) {
      this.host.scene.remove(e.obj);
      e.dispose();
    }
    this.effects = [];
    for (const p of this.projectiles.values()) {
      this.host.scene.remove(p.sprite);
      p.sprite.material.dispose();
    }
    this.projectiles.clear();
    this.areas.clear();
    this.add.clear();
    this.soft.clear();
  }
}
